import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ProductLaunch {
  enabled: boolean;
  productName: string;
  tagline: string;
  description: string;
  badge: string;
  launchDate: string;        // ISO datetime — countdown target
  ctaLabel: string;
  ctaUrl: string;
  image: string;
  accentColor: string;
  showCountdown: boolean;
  showNotifyForm: boolean;
  notifyFormUrl: string;
  specs: { label: string; value: string }[];
}

interface ProductLaunchContextValue {
  launch: ProductLaunch;
  updateLaunch: (l: ProductLaunch) => void;
}

const STORAGE_KEY = 'stingray_product_launch';

export const defaultLaunch: ProductLaunch = {
  enabled: false,
  productName: 'Stingray LiFePO4 300Ah',
  tagline: 'The Most Powerful Marine Battery We\'ve Ever Built.',
  description: 'Triple the capacity. Same compact footprint. The 300Ah LiFePO4 is engineered for offshore professionals who demand all-day power without compromise.',
  badge: 'Coming Soon',
  launchDate: (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString();
  })(),
  ctaLabel: 'Notify Me at Launch',
  ctaUrl: '/contact',
  image: 'https://readdy.ai/api/search-image?query=premium%20lithium%20marine%20battery%20300ah%20large%20black%20sleek%20futuristic%20design%20glowing%20orange%20accent%20lights%20dark%20studio%20background%20dramatic%20product%20photography%20high%20contrast%20sharp%20details%20next%20generation%20battery%20technology&width=800&height=600&seq=launch-product-hero&orientation=landscape',
  accentColor: '#F97316',
  showCountdown: true,
  showNotifyForm: true,
  notifyFormUrl: '',
  specs: [
    { label: 'Capacity', value: '300Ah' },
    { label: 'Voltage', value: '12.8V' },
    { label: 'Cycle Life', value: '2500+' },
    { label: 'Weight', value: '38 lbs' },
  ],
};

const ProductLaunchContext = createContext<ProductLaunchContextValue | null>(null);

function load(): ProductLaunch {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...defaultLaunch, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return defaultLaunch;
}

export function ProductLaunchProvider({ children }: { children: ReactNode }) {
  const [launch, setLaunch] = useState<ProductLaunch>(load);

  const updateLaunch = (l: ProductLaunch) => {
    setLaunch(l);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(l)); } catch { /* ignore */ }
  };

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try { setLaunch({ ...defaultLaunch, ...JSON.parse(e.newValue) }); } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <ProductLaunchContext.Provider value={{ launch, updateLaunch }}>
      {children}
    </ProductLaunchContext.Provider>
  );
}

export function useProductLaunch() {
  const ctx = useContext(ProductLaunchContext);
  if (!ctx) throw new Error('useProductLaunch must be used within ProductLaunchProvider');
  return ctx;
}
